import EventEmitter from 'eventemitter3';
import { withAppAiDiscovery } from '@yada/plugin-api';
import type {
  AppHostApi,
  AppManifest,
  AppModule,
  AppInstance,
  AppStorage,
  AppSyncApi,
  AppAgentApi,
  AppNotificationsApi,
  AppLlmApi,
  AppLocaleContext,
  UiBridge,
  TabPresence,
  UiSlots,
  WidgetDefinition,
  WidgetSlot,
  AppAiCapabilityDescriptor,
  AppAiContext,
  QueryHandlerMap,
} from '@yada/plugin-api';
import { QueryRegistry } from './QueryRegistry';
import { ExportRegistry } from './ExportRegistry';
import { ImportRegistry } from './ImportRegistry';
import type { RuntimeAdapters } from './types';

// Callers that still write into another app's storage through a bridge. Each is
// a known violation of the runtime constitution's cross-app rule; remove an entry
// when the caller moves to the owner's operations, and never add one.
const LEGACY_CROSS_APP_WRITERS: Record<string, string[]> = {
  '@yada/app-groups': ['@yada/app-chat'],
};

/** Another app's storage, readable but not writable by the caller. */
function readOnlyBridge(storage: AppStorage, callerAppId: string, targetAppId: string): AppStorage {
  if (LEGACY_CROSS_APP_WRITERS[callerAppId]?.includes(targetAppId)) return storage;
  const refuse = async (): Promise<never> => {
    throw new Error(
      `App ${callerAppId} cannot write to ${targetAppId} storage. ` +
      `Ask ${targetAppId} to make the change through an operation it registers with host.query.`
    );
  };
  return {
    get: (key) => storage.get(key),
    set: refuse,
    remove: refuse,
    keys: storage.keys ? () => storage.keys!() : undefined,
    onRemoteUpdate: storage.onRemoteUpdate,
  };
}

export interface RegistryEntry {
  manifest: AppManifest;
}

type RegisteredWidget = WidgetDefinition & { _appId: string };

export class Runtime {
  private registry = new Map<string, RegistryEntry>();
  active: { id: string; instance: AppInstance } | null = null;
  private emitter = new EventEmitter();
  private _syncCleanups = new Map<string, () => void>();

  readonly queryRegistry = new QueryRegistry();
  readonly exportRegistry = new ExportRegistry();
  readonly importRegistry = new ImportRegistry();

  private widgetSlotMap = new Map<WidgetSlot, RegisteredWidget[]>();
  // Hosts for app code that runs without the app being mounted: widgets, queries.
  private headlessHosts = new Map<string, AppHostApi>();

  constructor(private adapters: RuntimeAdapters) {
    this.queryRegistry.setResolver((appId) => this.loadHeadlessQueries(appId));
    if (adapters.backup?.exportFullBackup) {
      this.exportRegistry.register(
        '@yada/shell',
        'Full Account Backup',
        [{ format: 'json', label: 'Full account backup (JSON)', description: 'All your YADA data in one portable file' }],
        () => adapters.backup!.exportFullBackup!()
      );
    }
    if (adapters.backup?.importFullBackup) {
      this.importRegistry.register(
        '@yada/shell',
        'Full Account Backup',
        [{ format: 'json', label: 'Restore from full backup', description: 'Seed your account from a YADA backup file' }],
        (data) => adapters.backup!.importFullBackup!(data)
      );
    }
  }

  on(event: string, cb: (payload: any) => void): () => void {
    this.emitter.on(event, cb);
    return () => this.emitter.off(event, cb);
  }

  emit(event: string, payload?: unknown): void {
    this.emitter.emit(event, payload);
  }

  register(manifest: AppManifest): void {
    this.registry.set(manifest.id, { manifest });
    if (manifest.widgets?.length) {
      if (!this.headlessHosts.has(manifest.id)) {
        this.headlessHosts.set(manifest.id, this.createHost(manifest.id));
      }
      for (const widget of manifest.widgets) {
        const slot = widget.slot as WidgetSlot;
        const existing = this.widgetSlotMap.get(slot) ?? [];
        this.widgetSlotMap.set(slot, [...existing, { ...widget, _appId: manifest.id }]);
      }
      this.emitter.emit('widgets:changed');
    }
  }

  getWidgets(slot: WidgetSlot): WidgetDefinition[] {
    return (this.widgetSlotMap.get(slot) ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  getWidgetHost(widgetId: string): AppHostApi | null {
    for (const widgets of this.widgetSlotMap.values()) {
      const w = widgets.find((x) => x.id === widgetId);
      if (w) return this.headlessHosts.get(w._appId) ?? null;
    }
    return null;
  }

  list(isDeveloper?: boolean): AppManifest[] {
    const allApps = Array.from(this.registry.values()).map((r) => r.manifest);
    if (isDeveloper === undefined) return allApps;
    return allApps.filter((app) => !app.developerOnly || isDeveloper);
  }

  listAiCapabilities(): AppAiCapabilityDescriptor[] {
    return Array.from(this.registry.entries()).flatMap(([appId, entry]) => {
      const capabilities = entry.manifest.aiCapabilities;
      return capabilities ? [{ appId, appName: entry.manifest.name, ...capabilities }] : [];
    });
  }

  async importAiCapabilities(appId: string): Promise<AppAiContext | null> {
    const entry = this.registry.get(appId);
    if (!entry?.manifest.aiCapabilities) return null;
    const module = await entry.manifest.load();
    return module.getAiContext ? module.getAiContext(this.createHost(appId)) : null;
  }

  private async loadHeadlessQueries(appId: string): Promise<QueryHandlerMap | null> {
    const entry = this.registry.get(appId);
    if (!entry) return null;
    const module = await entry.manifest.load();
    if (!module.queries) return null;
    let host = this.headlessHosts.get(appId);
    if (!host) {
      host = this.createHost(appId);
      this.headlessHosts.set(appId, host);
    }
    return module.queries(host);
  }

  async getAgentContext(appId: string, allowedAppIds?: string[]): Promise<AppAiContext> {
    const context = this.active?.id === appId && this.active.instance.getAiContext
      ? await this.active.instance.getAiContext()
      : await this.importAiCapabilities(appId) ?? {};
    const allowed = () => this.listAiCapabilities().filter(app => (!allowedAppIds || allowedAppIds.includes(app.appId)) && (app.appId === appId || this.adapters.permissions?.isCrossAppAllowed?.(appId, app.appId) !== false));
    return withAppAiDiscovery(context, {
      list: allowed,
      import: target => allowed().some(app => app.appId === target) ? this.importAiCapabilities(target) : Promise.resolve(null),
    });
  }

  async activate(appId: string): Promise<void> {
    const entry = this.registry.get(appId);
    if (!entry) throw new Error(`App not found: ${appId}`);

    let mod: AppModule;
    try {
      mod = await entry.manifest.load();
    } catch (err) {
      console.error(`[Runtime] Failed to load app module for ${appId}:`, err);
      this.emitter.emit('toast', `App not available offline yet. Please connect to the internet once to download ${entry.manifest.name || appId}.`);
      throw err;
    }

    if (this.active) {
      this.exportRegistry.unregister(this.active.id);
      this.importRegistry.unregister(this.active.id);
      this.active.instance.unmount();
    }
    const host = this.createHost(appId);
    const instance = mod.mount(host);
    this.active = { id: appId, instance };
    this.emitter.emit('slots', entry.manifest.slots as UiSlots);
  }

  submitCommand(text: string): void {
    if (this.active?.instance.onCommand) this.active.instance.onCommand(text);
  }

  createHost(appId: string): AppHostApi {
    const entry = this.registry.get(appId);
    const metadataExtractor = entry?.manifest.metadataExtractor;
    const appName = entry?.manifest.name ?? appId;

    const storage: AppStorage = this.adapters.storage.createStorage(appId, metadataExtractor);

    this._syncCleanups.get(appId)?.();
    const _unsubAutoSync = storage.onRemoteUpdate?.(() => {
      const wsId = this.adapters.workspace?.getCurrentWorkspaceId() ?? 'personal';
      this.emitter.emit('sync:document-updated', {
        documentId: `${appId}-${wsId}`,
      });
    });
    if (_unsubAutoSync) this._syncCleanups.set(appId, _unsubAutoSync);

    const ui: UiBridge = {
      setTitle: (title) => this.emitter.emit('title', title),
      toast: (m) => this.emitter.emit('toast', m),
    };

    const events = {
      emit: (e: string, p?: unknown) => this.emitter.emit(e, p),
      on: (e: string, cb: (p: unknown) => void) => {
        this.emitter.on(e, cb as any);
        return () => this.emitter.off(e, cb as any);
      },
    };

    const tabs: TabPresence = {
      async claim() {
        return { state: 'owner', release() {} } as any;
      },
      onChange(cb) {
        cb('owner');
        return () => {};
      },
    };

    const workspaceActive = this.adapters.workspace?.isWorkspaceActive() ?? false;

    const sync: AppSyncApi = this.adapters.sync?.getSyncApi(appId) ?? {
      get state() { return { status: 'idle' as const }; },
      onChange() { return () => {}; },
      flush: async () => {},
    };

    const query = this.queryRegistry.createApi(appId);
    const exportApi = this.exportRegistry.createApi(appId, appName);
    const importApi = this.importRegistry.createApi(appId, appName);

    const notifications: AppNotificationsApi = this.adapters.notifications?.createApi(appId) ?? {
      get permission() { return 'default' as const; },
      requestPermission: async () => 'default' as const,
      schedule: async () => {},
      cancel: async () => {},
      cancelAll: async () => {},
      onActivate: () => () => {},
    };

    const llm: AppLlmApi = this.adapters.llm?.createApi(appId, appName) ?? {
      get provider() { return null; },
      get isLocal() { return false; },
      complete: async () => null,
    };

    let _agentLoading = false;
    const llmAdapter = this.adapters.llm;
    const agent: AppAgentApi = {
      get isLoading() { return _agentLoading; },
      async invoke(request, onAccept) {
        if (!llmAdapter?.invokeAgent) return;
        _agentLoading = true;
        try {
          await llmAdapter.invokeAgent(appId, appName, request, onAccept);
        } finally {
          _agentLoading = false;
        }
      },
    };

    const aiCapabilities = {
      list: () => this.listAiCapabilities(),
      import: (targetAppId: string) => this.importAiCapabilities(targetAppId),
    };

    const locale: AppLocaleContext = this.adapters.locale?.getLocaleContext() ?? {
      language: 'en',
      country: {
        code: 'US',
        englishName: 'United States',
        flag: '🇺🇸',
        currency: 'USD',
        currencySymbol: '$',
        dateFormat: 'YMD',
        decimalSeparator: '.',
      },
      countryFeatures: {
        ksef: false,
        eurozone: false,
        euMember: false,
        leftHandTraffic: false,
        measurementSystem: 'metric',
        firstDayOfWeek: 0,
      },
      onChange: () => () => {},
    };

    const checkCrossAppPermission = (targetAppId: string) => {
      if (targetAppId === appId) return;
      const allowed = entry?.manifest.permissions?.crossAppStorage || [];
      if (!allowed.includes(targetAppId)) {
        const errorMsg = `Security Error: App ${appId} attempted to access unauthorized storage namespace ${targetAppId}. ` +
                        `Must declare ${targetAppId} in manifest.permissions.crossAppStorage.`;
        console.error(`[Runtime] ${errorMsg}`);
        throw new Error(errorMsg);
      }
      if (this.adapters.permissions?.isCrossAppAllowed && !this.adapters.permissions.isCrossAppAllowed(appId, targetAppId)) {
        const errorMsg = `Permission Denied: User has disabled cross-app data sharing from ${appId} to ${targetAppId}.`;
        console.warn(`[Runtime] ${errorMsg}`);
        throw new Error(errorMsg);
      }
    };

    // One bridge per target, so callers get a stable reference to use in hook dependencies.
    const bridges = new Map<string, AppStorage>();
    const bridgeTo = (key: string, targetAppId: string, open: () => AppStorage): AppStorage => {
      let bridge = bridges.get(key);
      if (!bridge) {
        bridge = readOnlyBridge(open(), appId, targetAppId);
        bridges.set(key, bridge);
      }
      return bridge;
    };

    const getStorageFor = (targetAppId: string) => {
      checkCrossAppPermission(targetAppId);
      if (targetAppId === appId) return this.adapters.storage.createStorage(targetAppId);
      return bridgeTo(targetAppId, targetAppId, () => this.adapters.storage.createStorage(targetAppId));
    };

    const rawWorkspace = this.adapters.workspace?.createWorkspaceApi?.(appId);
    const workspace = rawWorkspace ? {
      ...rawWorkspace,
      getStorageForWorkspace: (wsAppId: string, wsId: string) => {
        checkCrossAppPermission(wsAppId);
        if (wsAppId === appId) return rawWorkspace.getStorageForWorkspace(wsAppId, wsId);
        return bridgeTo(`${wsAppId}@${wsId}`, wsAppId, () => rawWorkspace.getStorageForWorkspace(wsAppId, wsId));
      },
    } : undefined;
    const weather = this.adapters.weather?.getWeatherService() ?? null;
    const profile = this.adapters.profile?.createApi?.(appId) ?? undefined;
    const api = this.adapters.api?.createApi(appId);

    return {
      storage,
      ui,
      events,
      tabs,
      workspaceActive,
      sync,
      query,
      export: exportApi,
      import: importApi,
      notifications,
      llm,
      agent,
      aiCapabilities,
      getStorageFor,
      locale,
      workspace,
      weather,
      profile,
      api,
    };
  }

  notifyLocaleChange(): void {
    this.emitter.emit('locale:change');
  }
}
